import { useState, type FC } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Row,
  Skeleton,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { SessionQualification } from '@evimo/types/enums/session-qualification';
import { humanizeStoredToken } from '@evimo/types/constants/session/session.labels';
import { shouldShowAnomalyQualification } from '~/app/components/session/utils/qualificationDisplay';
import {
  ArrowLeftOutlined,
  ExperimentOutlined,
  HistoryOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useSessionCharge,
  useSessionTransitions,
  useSessionProblems,
  useSessionReadings,
} from '~/app/components/session/hooks/useSessionQueries';
import { useActivityWords } from '~/app/components/session/hooks/useActivityWords';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import {
  useProblemColumns,
  useReadingColumns,
  useTransitionColumns,
} from './hooks/useChargeDetailColumns';

const { Paragraph, Title } = Typography;

export interface ChargeDetailProps {
  chargeId: string;
}

export const ChargeDetail: FC<ChargeDetailProps> = ({ chargeId }) => {
  const { t } = useTranslation('sessions');
  const words = useActivityWords();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>('transitions');
  const [transitionsPage, setTransitionsPage] = useState<number>(1);
  const [transitionsPageSize, setTransitionsPageSize] = useState<number>(20);
  const [problemsPage, setProblemsPage] = useState<number>(1);
  const [problemsPageSize, setProblemsPageSize] = useState<number>(20);
  const [readingsPage, setReadingsPage] = useState<number>(1);
  const [readingsPageSize, setReadingsPageSize] = useState<number>(20);

  const chargeQuery = useSessionCharge(chargeId);
  const resolvedChargeId = chargeQuery.data?.id ?? '';
  const transitionsQuery = useSessionTransitions(
    resolvedChargeId,
    activeTab === 'transitions',
    transitionsPage,
    transitionsPageSize,
  );
  const problemsQuery = useSessionProblems(
    resolvedChargeId,
    activeTab === 'problems',
    problemsPage,
    problemsPageSize,
  );
  const readingsQuery = useSessionReadings(
    resolvedChargeId,
    activeTab === 'readings',
    readingsPage,
    readingsPageSize,
  );

  const transitionColumns = useTransitionColumns();
  const problemColumns = useProblemColumns();
  const readingColumns = useReadingColumns();

  const charge = chargeQuery.data;
  const transitions = transitionsQuery.data?.data ?? [];
  const transitionsMeta = transitionsQuery.data?.meta;
  const problems = problemsQuery.data?.data ?? [];
  const problemsMeta = problemsQuery.data?.meta;
  const readings = readingsQuery.data?.data ?? [];
  const readingsMeta = readingsQuery.data?.meta;

  const tabItems = charge
    ? [
        {
          key: 'transitions',
          label: (
            <Flex gap={8} align="center">
              <HistoryOutlined />
              <span>{t('whatTheChargerDid')}</span>
            </Flex>
          ),
          children: (
            <Card variant="outlined" style={{ borderRadius: 12 }}>
              <Paragraph type="secondary">
                {t('whatTheChargerDidHelp')}
              </Paragraph>
              <EvimoTable
                rowKey={(row) => `${row.startedAt}-${row.status}`}
                columns={transitionColumns}
                dataSource={transitions}
                loading={transitionsQuery.isLoading}
                pagination={{
                  current: transitionsPage,
                  pageSize: transitionsPageSize,
                  total: transitionsMeta?.total ?? 0,
                  onChange: (page, pageSize) => {
                    if (pageSize !== transitionsPageSize) {
                      setTransitionsPageSize(pageSize);
                      setTransitionsPage(1);
                      return;
                    }
                    setTransitionsPage(page);
                  },
                }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          ),
        },
        {
          key: 'problems',
          label: (
            <Flex gap={8} align="center">
              <WarningOutlined />
              <span>{t('problems')}</span>
            </Flex>
          ),
          children: (
            <Card variant="outlined" style={{ borderRadius: 12 }}>
              <EvimoTable
                rowKey={(row) => `${row.startedAt}-${row.faultType}`}
                columns={problemColumns}
                dataSource={problems}
                loading={problemsQuery.isLoading}
                pagination={{
                  current: problemsPage,
                  pageSize: problemsPageSize,
                  total: problemsMeta?.total ?? 0,
                  onChange: (page, pageSize) => {
                    if (pageSize !== problemsPageSize) {
                      setProblemsPageSize(pageSize);
                      setProblemsPage(1);
                      return;
                    }
                    setProblemsPage(page);
                  },
                }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={t('none')} /> }}
              />
            </Card>
          ),
        },
        {
          key: 'readings',
          label: (
            <Flex gap={8} align="center">
              <ExperimentOutlined />
              <span>{t('readings')}</span>
            </Flex>
          ),
          children: (
            <Card variant="outlined" style={{ borderRadius: 12 }}>
              <Paragraph type="secondary">{t('readingsHelp')}</Paragraph>
              <EvimoTable
                rowKey={(row) =>
                  `${row.recordedAt}-${row.parameter}-${row.measureType}`
                }
                columns={readingColumns}
                dataSource={readings}
                loading={readingsQuery.isLoading}
                pagination={{
                  current: readingsPage,
                  pageSize: readingsPageSize,
                  total: readingsMeta?.total ?? 0,
                  onChange: (page, pageSize) => {
                    if (pageSize !== readingsPageSize) {
                      setReadingsPageSize(pageSize);
                      setReadingsPage(1);
                      return;
                    }
                    setReadingsPage(page);
                  },
                }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          ),
        },
      ]
    : [];

  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      {/* Top Left Back Button */}
      <Flex align="center" justify="flex-start">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/sessions')}
          style={{ paddingLeft: 0, fontWeight: 500 }}
        >
          {t('back')}
        </Button>
      </Flex>

      {chargeQuery.isError ? (
        <Alert type="error" showIcon message={t('loadError')} />
      ) : null}

      {!chargeQuery.isLoading && !charge && !chargeQuery.isError ? (
        <Empty description={t('emptyCharges')} />
      ) : null}

      {chargeQuery.isLoading ? (
        <Card variant="outlined" style={{ borderRadius: 12 }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      ) : null}

      {charge ? (
        <>
          <Card variant="outlined" style={{ borderRadius: 12 }}>
            <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
              {words.name(charge.chargerName)}
              {charge.connectorName
                ? `, ${words.name(charge.connectorName)}`
                : ''}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 20 }}>
              {words.name(charge.siteName)}
              {' · '}
              {charge.startedAt ? words.dayClock(charge.startedAt) : '-'}
              {charge.accessCard ? ` · ${words.name(charge.accessCard)}` : ''}
            </Paragraph>
            <Row gutter={[24, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('columns.sessionId')}
                  value={
                    charge.identifier?.trim()
                      ? charge.identifier.trim()
                      : t('unknown')
                  }
                />
                {charge.transactionId?.trim() ? (
                  <Typography.Text
                    type="secondary"
                    style={{ display: 'block', marginTop: 4, fontSize: 14 }}
                  >
                    {t('transactionNumber', {
                      number: charge.transactionId.trim(),
                    })}
                  </Typography.Text>
                ) : null}
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('columns.started')}
                  value={words.clock(charge.startedAt)}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('columns.ended')}
                  value={words.clock(charge.stoppedAt)}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title={t('columns.energy')}
                  value={words.energy(charge.energyKwh)}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Statistic
                  title={t('columns.howItEnded')}
                  value={words.sessionStatusLabel(charge)}
                />
              </Col>
              {shouldShowAnomalyQualification(
                charge.qualification as SessionQualification | null,
              ) ? (
                <Col xs={24} sm={12}>
                  <Statistic
                    title={t('columns.anomalyDetected')}
                    value={words.qualificationLabel(
                      charge.qualification as SessionQualification,
                    )}
                    formatter={(value) => {
                      const q = charge.qualification as SessionQualification;
                      const tag = (
                        <Tag color={words.qualificationColor(q)}>{value}</Tag>
                      );
                      const hint = words.qualificationTooltip(q);
                      return hint ? <Tooltip title={hint}>{tag}</Tooltip> : tag;
                    }}
                  />
                </Col>
              ) : null}
            </Row>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 2 }}
              style={{ marginTop: 24 }}
              title={t('detail.ocppTitle')}
            >
              <Descriptions.Item label={t('detail.lastStatus')}>
                {charge.lastStatus
                  ? words.status(charge.lastStatus)
                  : t('unknown')}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.startIdTagStatus')}>
                {charge.startIdTagStatus?.trim()
                  ? humanizeStoredToken(charge.startIdTagStatus)
                  : t('unknown')}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.stopReason')}>
                {charge.stopReason?.trim()
                  ? humanizeStoredToken(charge.stopReason)
                  : t('unknown')}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.hasStopTransaction')}>
                {charge.hasStopTransaction ? t('detail.yes') : t('detail.no')}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Separate Section Tabs */}
          <Tabs
            type="card"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{ marginTop: 8 }}
          />
        </>
      ) : null}
    </Flex>
  );
};
